import React from 'react';
import { X } from 'lucide-react';

interface PrivacyProps {
  isOpen: boolean;
  onClose: () => void;
}

function Privacy({ isOpen, onClose }: PrivacyProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-start justify-center px-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(1rem+env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain touch-pan-y">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden my-auto">
        <div className="p-4 md:p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h1 className="text-2xl font-bold">Privacy Policy</h1>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close privacy policy"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="overflow-y-auto p-4 md:p-6">
          <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

          <div className="prose prose-blue max-w-none">
            <h2 className="text-xl font-semibold mt-8 mb-4">1. Information We Collect</h2>
            <p className="mb-4">We collect the minimal data necessary to provide our services. This may include:</p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Basic usage data such as IP addresses, browser types, and site usage metrics.</li>
              <li className="mb-2">Any information you voluntarily submit (e.g., your queries).</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">To operate, maintain, and improve our services.</li>
              <li className="mb-2">To analyze usage trends and enhance user experience.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. Cookies and Tracking Technologies</h2>
            <p className="mb-6">
              We may use cookies or similar technologies to remember user preferences, deliver relevant content, and analyze site traffic. You can disable cookies in your browser settings; however, some features of our service may not function properly if you do so.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Data Sharing and Disclosure</h2>
            <p className="mb-4">We do not sell or rent personal information. We only share data under the following circumstances:</p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">When required by law, regulation, or legal request.</li>
              <li className="mb-2">To protect our rights, property, or safety (e.g., to address fraud or security issues).</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. Security</h2>
            <p className="mb-6">
              We implement reasonable measures to help protect your information from unauthorized access or disclosure. However, no system can guarantee absolute security, and we cannot ensure or warrant the security of any information you transmit to us.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Children's Privacy</h2>
            <p className="mb-6">
              Our service is not intended for children under 13. We do not knowingly collect personal information from children. If you become aware that a child has provided personal data, please contact us so we can remove that information.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Changes to This Policy</h2>
            <p className="mb-6">
              We may update this Privacy Policy from time to time. When we do, we will revise the "Last Updated" date at the top of this page. Any material changes will be posted here, and we encourage you to review the policy periodically.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Contact Us</h2>
            <p className="mb-4">If you have questions about this Privacy Policy or how we handle your data, you may contact:</p>
            <ul className="list-none mb-6">
              <li><strong>Name:</strong> Terrez Wells</li>
              <li><strong>Email:</strong> tezwells5@gmail.com</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Privacy;